"use client";

import { MADRID_PHONE } from "@/data/contact";
import { useEffect, useState } from "react";
import Image from "next/image";
import { Button, buttonVariants } from "@/components/ui/button";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import BackToTopButton from "@/components/ui/back-to-top-button";
import { Link } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { HUBSPOT_FORMS, HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";
import { LocationInstallationsGallery } from "@/components/locations/location-installations-gallery";
import { useTranslations } from "next-intl";

type Highlight = { title: string; description: string; icon: string };
type Step = { title: string; subtitle: string; description: string };
type SeoCard = { text: string; icon: string };
type GoogleReviewsPayload = {
  rating: number | null;
  userRatingsTotal: number | null;
};

const HIGHLIGHT_ICONS = [
  "/static/icons/valoraciones.webp",
  "/static/icons/manos-unidas.webp",
  "/static/icons/asistente.webp",
  "/static/icons/dinero.webp",
  "/static/icons/mano.webp",
  "/static/icons/bombilla.webp",
];

export default function ContactoMadridPage({ locale }: { locale: string }) {
  const t = useTranslations("madridPage");
  const basePath = locale === routing.defaultLocale ? "" : `/${locale}`;
  const portalId = HUBSPOT_PORTAL_ID;
  const formId = HUBSPOT_FORMS.contact;
  const [googleData, setGoogleData] = useState<GoogleReviewsPayload | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchReviews = async () => {
      try {
        const response = await fetch("/api/google-reviews");
        if (!response.ok) return;
        const data = (await response.json()) as GoogleReviewsPayload;
        if (isMounted) {
          setGoogleData(data);
        }
      } catch {
        // Silently fail, keep default rating
      }
    };
    fetchReviews();
    return () => {
      isMounted = false;
    };
  }, []);

  const displayRating = googleData?.rating?.toFixed(1) ?? "4.7";
  const reviewCount = googleData?.userRatingsTotal ?? 7000;

  const highlights: Highlight[] = HIGHLIGHT_ICONS.map((icon, idx) => ({
    title: t(`highlights.${idx}.title`),
    description: t(`highlights.${idx}.description`),
    icon,
  }));

  const steps: Step[] = Array.from({ length: 6 }).map((_, idx) => ({
    title: t(`steps.${idx}.title`),
    subtitle: t(`steps.${idx}.subtitle`),
    description: t(`steps.${idx}.description`),
  }));

  const introCards: SeoCard[] = [
    { text: t("intro.paragraphOne"), icon: "/static/icons/manos-unidas.webp" },
    { text: t("intro.paragraphTwo"), icon: "/static/icons/documentos.webp" },
  ];

  const installationCards: SeoCard[] = [
    { text: t("installationSection.paragraphOne"), icon: "/static/icons/mano.webp" },
    { text: t("installationSection.paragraphTwo"), icon: "/static/icons/bombilla.webp" },
  ];

  const budgetCards: SeoCard[] = [
    { text: t("budgetSection.paragraphOne"), icon: "/static/icons/dinero.webp" },
    { text: t("budgetSection.paragraphTwo"), icon: "/static/icons/valoraciones.webp" },
  ];

  const renderWithEmphasis = (text: string, strongClassName: string) =>
    text.split(/(\*\*.*?\*\*)/g).map((chunk, idx) =>
      chunk.startsWith("**") && chunk.endsWith("**") ? (
        <strong key={`strong-${idx}`} className={strongClassName}>
          {chunk.slice(2, -2)}
        </strong>
      ) : (
        <span key={`text-${idx}`}>{chunk}</span>
      )
    );

  return (
    <div className="min-h-screen bg-surface-light text-ink dark:bg-surface-dark dark:text-white">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/static/services/autoconsumo-domestico/dos-hermanas-sevilla-02.webp"
            alt={t("hero.alt")}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy-deep/80 via-brand-navy-deep/70 to-brand-navy-deep/90" />
        </div>
        <div className="relative mx-auto flex max-w-6xl flex-col items-center px-4 py-16 text-center text-white sm:py-20">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-display text-gold-soft">
            {t("hero.eyebrow")}
          </p>
          <h1 className="mt-3 type-h1">{t("hero.title")}</h1>
          <p className="mt-4 max-w-2xl text-sm sm:text-lg text-mist">{t("hero.subtitle")}</p>
          <Button asChild className="mt-6 rounded-full bg-white px-6 py-3 text-sm font-semibold text-brand-navy shadow-lg hover:bg-white/90">
            <a href="#madrid-form">{t("hero.cta")}</a>
          </Button>
          <div className="mt-6 rounded-2xl bg-white/95 px-6 py-4 text-ink shadow-xl">
            <div className="flex items-center justify-center gap-2">
              <div className="relative h-6 w-28">
                <svg viewBox="0 0 140 24" className="h-full w-full" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <path
                      key={index}
                      d="M12 2l2.4 7.4h7.6l-6 4.6 2.4 7.4-6-4.6-6 4.6 2.4-7.4-6-4.6h7.6z"
                      transform={`translate(${index * 28}, 0)`}
                      fill="#FFC107"
                    />
                  ))}
                </svg>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-brand-navy">
                {t("reviews.ratingLabel", { rating: displayRating })}
              </span>
            </div>
            <p className="mt-2 text-[11px] sm:text-xs text-gray-600">
              {t("reviews.countLabel", { count: reviewCount.toLocaleString() })}
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-8 sm:section-y">
        <div className="surface-card mx-auto max-w-6xl p-6 sm:p-8">
          <div className="grid gap-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {introCards.map((item, idx) => (
                <article
                  key={`intro-card-${idx}`}
                  className="rounded-2xl bg-surface-light p-5 text-sm sm:text-base leading-relaxed text-gray-700 dark:bg-surface-dark dark:text-gray-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-navy to-brand-navy-light p-3 shadow-lg shadow-brand-navy/25">
                      <Image src={item.icon} alt="" width={30} height={30} className="brightness-0 invert" aria-hidden />
                    </div>
                    <p>{renderWithEmphasis(item.text, "font-semibold text-brand-navy dark:text-white")}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
        <Button asChild className={buttonVariants({ variant: "pillNavy", size: "pill", className: "mt-5" })}>    
          <Link href={`${basePath}/presupuesto-placas-solares`}>{t("hero.cta")}</Link>
        </Button>
        <br className="my-10" />
      </div>

      <section className="px-4 pb-8 sm:pb-10">
        <div className="mx-auto max-w-6xl rounded-card bg-gradient-to-br from-brand-navy to-brand-navy-light p-6 text-white shadow-card sm:p-8">
          <div className="grid gap-6">
            <h2 className="type-h2">{t("installationSection.title")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {installationCards.map((item, idx) => (
                <article
                  key={`installation-card-${idx}`}
                  className="rounded-2xl bg-white/12 p-5 text-sm sm:text-base leading-relaxed text-white/90"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 p-3">
                      <Image src={item.icon} alt="" width={30} height={30} className="brightness-0 invert" aria-hidden />
                    </div>
                    <p>{renderWithEmphasis(item.text, "font-semibold text-white")}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-10">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative min-h-[260px] overflow-hidden rounded-card shadow-lg">
            <Image
              src="/static/services/autoconsumo-domestico/carmona-sevilla.webp"
              alt={t("about.imageAlt")}
              fill
              className="object-cover"
            />
          </div>
          <div className="surface-card p-6">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">
              {t("about.eyebrow")}
            </p>
            <h3 className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">
              {t("about.title")}
            </h3>
            <div className="mt-5 flex flex-col gap-3 text-sm text-gray-700 dark:text-gray-200">
              <p>
                <strong>{t("about.phoneLabel")} </strong>
                <a href={MADRID_PHONE.href} className="text-brand-red">
                  {t("about.phoneNumber")}
                </a>
              </p>
              <p>
                <strong>{t("about.addressLabel")} </strong>
                {t("about.address")}
              </p>
              <Button asChild className="mt-6 w-full rounded-full px-6 py-3 text-sm font-semibold">
                <Link href={MADRID_PHONE.href}>{t("formBlock.sidebarCall")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LocationInstallationsGallery city="Madrid" />

      <section id="madrid-form" className="scroll-mt-24 px-4 pb-12">
        <div className="mx-auto grid max-w-6xl gap-6">
          <div className="surface-card p-6">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{t("formBlock.eyebrow")}</p>
            <h3 className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{t("formBlock.title")}</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{t("formBlock.subtitle")}</p>
            <div className="hubspot-light mt-6 rounded-card bg-surface-light p-4 dark:bg-surface-dark-raised">
              <HubSpotForm portalId={portalId} formId={formId} region={HUBSPOT_REGION} />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-brand-navy to-brand-navy-light px-4 section-y text-white">
        <div className="mx-auto max-w-6xl text-center">
          <h3 className="text-2xl sm:text-3xl font-black">{t("benefits.title")}</h3>
          <p className="mt-3 text-sm sm:text-base text-white/80">{t("benefits.subtitle")}</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {highlights.map((item) => (
              <div key={item.title} className="rounded-2xl bg-white/12 p-5 text-left shadow-xl backdrop-blur">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20">
                    <Image src={item.icon} alt={item.title} width={36} height={36} className="brightness-0 invert" />
                  </div>
                  <h4 className="text-lg sm:text-xl font-semibold">{item.title}</h4>
                </div>
                <p className="mt-3 text-sm text-white/85">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 section-y">
        <div className="mx-auto max-w-6xl">
          <h3 className="text-center text-2xl sm:text-3xl font-black text-brand-navy dark:text-white">
            {t("steps.title")}
          </h3>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">{t("steps.subtitle")}</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-surface-dark-raised"
              >
                <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{step.title}</p>
                <h4 className="mt-2 text-lg font-bold text-brand-navy dark:text-white">{step.subtitle}</h4>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-14">
        <div className="mx-auto max-w-6xl grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="surface-card p-6">
            <h3 className="text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{t("ctaBlock.title")}</h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{t("ctaBlock.copy")}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild className={buttonVariants({ variant: "pill", size: "pill" })}>
                <Link href={`${basePath}/estudio-gratis`}>{t("ctaBlock.primary")}</Link>
              </Button>
              <Button
                variant="outline"
                className="rounded-full border-brand-navy px-6 py-3 text-sm font-semibold text-brand-navy hover:bg-brand-navy hover:text-white dark:border-white dark:text-white"
              >
                <Link href={`${basePath}/contacto`}>{t("ctaBlock.secondary")}</Link>
              </Button>
            </div>
          </div>
          <div className="relative min-h-[260px] overflow-hidden rounded-card shadow-lg">
            <Image
              src="/static/services/autoconsumo-domestico/dos-hermanas-sevilla-01.webp"
              alt={t("ctaBlock.imageAlt")}
              fill
              className="object-cover"
            />
          </div>
        </div>
      </section>

            <section className="px-4 pb-8 sm:pb-10">
        <div className="surface-card mx-auto max-w-6xl p-6 sm:p-8">
          <div className="grid gap-6">
            <h2 className="text-brand-navy dark:text-white type-h2">{t("budgetSection.title")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {budgetCards.map((item, idx) => (
                <article
                  key={`budget-card-${idx}`}
                  className="rounded-2xl bg-surface-light p-5 text-sm sm:text-base leading-relaxed text-gray-700 dark:bg-surface-dark dark:text-gray-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-navy to-brand-navy-light p-3 shadow-lg shadow-brand-navy/25">
                      <Image src={item.icon} alt="" width={30} height={30} className="brightness-0 invert" aria-hidden />
                    </div>
                    <p>{renderWithEmphasis(item.text, "font-semibold text-brand-navy dark:text-white")}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-10 sm:pb-12">
        <div className="surface-card-soft mx-auto max-w-6xl border border-brand-navy/15 p-6 dark:border-white/10 sm:p-8">
          <div className="grid gap-5 lg:grid-cols-[0.4fr_0.6fr] lg:items-start">
            <h3 className="text-2xl sm:text-3xl font-black text-brand-navy dark:text-white">{t("expertsSection.title")}</h3>
            <article className="rounded-2xl bg-white p-5 text-sm sm:text-base leading-relaxed text-gray-700 dark:bg-surface-dark dark:text-gray-300">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-navy to-brand-navy-light p-3 shadow-lg shadow-brand-navy/25">
                  <Image src="/static/icons/asistente.webp" alt="" width={30} height={30} className="brightness-0 invert" aria-hidden />
                </div>
                <p>{renderWithEmphasis(t("expertsSection.paragraph"), "font-semibold text-brand-navy dark:text-white")}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <BackToTopButton />
    </div>
  );
}
